import moment, { Moment } from 'moment';
import buildQuery from 'odata-query';
import { firstValueFrom, of } from 'rxjs';
import { fetchOdataAll } from '../../common/odataUtil';
import { empty, getDateTimeString, getStringDateTime, handleApiError } from '../../common/util';
import { IRepairOrder, IRepairOrderAllResponse, IRepairOrderDetail, IRepairOrderSummary } from '../../models/dms/repairOrder.model';
import { ITechComment } from '../../models/dms/technicianComments.model';
import axiosDmsInstance from './axiosDmsInstance';
import { IRepairOrderSort, RepairOrderOrderByField } from '../../models/dms/repairOrder.model';
import axios, { CancelTokenSource } from 'axios';
import { IOdataResult } from '../../models/odata.model';

export const getRepairOrdersAll = async (lastUpdatedTs: number, databaseOffsetMinutes: number, days: number = 60) => {
  try {
    const lastUpdatedDate = moment(lastUpdatedTs);
    const localOffsetMinutes = moment().utcOffset();

    // Adjust the last modified date to be inline with the timezone of the selected database
    const dateFormat = 'YYYY-MM-DDTHH:mm:ss';
    const offsetMinutes = localOffsetMinutes - databaseOffsetMinutes;
    const lastUpdatedDateWithOffset = lastUpdatedDate.subtract(offsetMinutes, 'minutes').format(dateFormat);

    const filterValid = {
      and: [{ IsClosed: { eq: false } }, { IsQuotation: { eq: false } }, { IsConfirmed: { eq: true } }, { IsInvoiced: { eq: false } }, { IsVetted: { eq: true } }, `LastUpdated gt DateTime'${lastUpdatedDateWithOffset}'`],
    };
    let filterInvalid = {
      and: [`LastUpdated gt DateTime'${lastUpdatedDateWithOffset}'`],
      or: [{ IsClosed: { eq: true } }, { IsQuotation: { eq: true } }, { IsConfirmed: { eq: false } }, { IsInvoiced: { eq: true } }, { IsVetted: { eq: false } }],
    };

    // This will stop the invalid ROs getting any results the first time around
    if (!lastUpdatedTs) {
      filterInvalid.and.push(`RepairOrderNumber lt 0`);
    }

    const queryValidRos = buildQuery({ filter: filterValid });
    const queryInvalidRos = buildQuery({ filter: filterInvalid });

    const startDate = new Date(+new Date() - days * 24 * 60 * 60 * 1000);
    const endDate = new Date(+new Date() + days * 24 * 60 * 60 * 1000);
    const options = {
      $inlinecount: 'allpages',
      startDate: getDateTimeString(startDate),
      endDate: getDateTimeString(endDate),
    };

    try {
      //const roSearch = await (repairOrderNumber ? getRepairOrderByRepairOrderNumber(repairOrderNumber) : empty<IRepairOrderDetail>());
      const validRos = await fetchOdataAll<IRepairOrder>(axiosDmsInstance, `/api/workshop/repairOrder/v1/all/${queryValidRos}`, options);
      const invalidRos = await fetchOdataAll<IRepairOrder>(axiosDmsInstance, `/api/workshop/repairOrder/v1/all/${queryInvalidRos}`, options);
      const deletedRos = await (lastUpdatedTs ? getDeletedRepairOrders(moment(lastUpdatedDateWithOffset, dateFormat).toDate()) : empty<number[]>([] as number[]));

      const repairOrderAllResponse = {
        //roSearch: mapRepairOrderDetailToRepairOrderWithJobs(roSearch),
        validRos: validRos.data,
        invalidRos: invalidRos.data,
        deletedRos,
      } as IRepairOrderAllResponse;

      return firstValueFrom(of(repairOrderAllResponse));
    } catch (ex) {
      return firstValueFrom(of({} as IRepairOrderAllResponse));
    }
  } catch (err: any) {
    return handleApiError('getRepairOrdersAll', err);
  }
};

export const getRepairOrderByRepairOrderNumber = async (repairOrderNumber: number) => {
  try {
    const url = `/api/workshop/repairOrder/v1/${repairOrderNumber}/detail`;
    let params = {} as any;

    const response = await axiosDmsInstance.get<IRepairOrderDetail>(`${url}`, {
      params,
    });

    return response.data;
  } catch (err: any) {
    return handleApiError('getRepairOrderByRepairOrderNumber', err);
  }
};

export const getDeletedRepairOrders = async (startDate: Date) => {
  try {
    const url = `/api/workshop/repairOrder/v1/all/deleted`;
    let params = {
      startDate,
    } as any;

    const response = await axiosDmsInstance.get<number[]>(`${url}`, {
      params,
    });

    return response.data;
  } catch (err: any) {
    return handleApiError('getDeletedRepairOrders', err);
  }
};

export const getTechnicianCommentsByRepairOrderNumber = async (repairOrderNumber: number) => {
  try {
    const url = `/api/workshop/repairOrder/v1/${repairOrderNumber}/techComments`;
    let params = {} as any;

    const response = await axiosDmsInstance.get<ITechComment[]>(`${url}`, {
      params,
    });

    return response.data;
  } catch (err: any) {
    return handleApiError('getTechnicianCommentsByRepairOrderNumber', err);
  }
};

export let getRepairOrderSummaryByKeywordCancelTokenSource: CancelTokenSource;
export const getRepairOrderSummaryByKeyword = async (keyword: string) => {
  try {
    const url = `api/workshop/repairOrder/v2/search`;
    let params = {
      $inlinecount: 'allpages',
      // searchPredicate: '',
    } as any;
    const filterValid = {
      and: [
        { 'Status/IsClosed': { eq: false } },
        { 'Status/IsQuotation': { eq: false } },
        { 'Status/IsConfirmed': { eq: true } },
        { 'Status/IsInvoiced': { eq: false } },
        { 'Status/IsVetted': { eq: true } },
        {
          or: [{ 'Vehicle/Source': 'VHS' }, { 'Vehicle/Source': 'WKV' }, { 'Vehicle/Source': 'FAS' }],
        },
      ],
    };

    if (keyword) {
      params.keyword = keyword;
    }

    const queryValidRos = buildQuery({ filter: filterValid });

    // Cancelation token
    getRepairOrderSummaryByKeywordCancelTokenSource?.cancel(`CANCEL getRepairOrderSummaryByKeyword`);
    getRepairOrderSummaryByKeywordCancelTokenSource = axios.CancelToken.source();

    const response = await axiosDmsInstance.get<IOdataResult<IRepairOrderSummary>>(`${url}/${queryValidRos}`, {
      params,
      cancelToken: getRepairOrderSummaryByKeywordCancelTokenSource.token,
    });

    return response.data;
  } catch (err: any) {
    return handleApiError('getRepairOrderSummaryByKeyword', err);
  }
};

export const filterAndSortRepairOrders = (repairOrders: IRepairOrder[], keyword: string = '', branchCode: string = '', techCode: string = '', startDateTime?: Moment, endDateTime?: Moment, orderBy?: IRepairOrderSort) => {
  keyword = keyword || '';
  branchCode = branchCode || '';
  techCode = techCode || '';

  keyword = keyword.toLowerCase();
  branchCode = branchCode.toLowerCase();
  techCode = techCode.toLowerCase();

  const filteredRepairOrders = repairOrders.filter((repairOrder: IRepairOrder) => {
    const jobList = repairOrder.jobs.reduce((prev, curr) => {
      return prev + curr.jobCode;
    }, '');
    const dropOffDateTime = getStringDateTime(repairOrder.dropOffDateTime);
    const pickUpDateTime = getStringDateTime(repairOrder.pickUpDateTime);

    let filterRo =
      ((repairOrder.repairOrderNumber || -1).toString().includes(keyword) ||
        (repairOrder.registrationNumber || '').toLowerCase().includes(keyword) ||
        (repairOrder.driver.name || '').toLowerCase().includes(keyword) ||
        (repairOrder.driver.companyName || '').toLowerCase().includes(keyword) ||
        (repairOrder.vehicle.vin || '').toLowerCase().includes(keyword) ||
        (repairOrder.vehicle.fleetNumber || '').toLowerCase().includes(keyword) ||
        jobList.toLowerCase().includes(keyword)) &&
      (branchCode === '' || (repairOrder.branch || '').toLowerCase() === branchCode) &&
      (techCode === '' || repairOrder.jobs?.some((job) => job.schedules?.some((schedule) => (schedule.techCode || '').toLowerCase() === techCode))) &&
      (startDateTime === undefined ||
        endDateTime === undefined ||
        (dropOffDateTime.isSameOrAfter(startDateTime) && dropOffDateTime.isSameOrBefore(endDateTime)) ||
        (pickUpDateTime.isSameOrAfter(startDateTime) && pickUpDateTime.isSameOrBefore(endDateTime)));

    if (!keyword) {
      filterRo = filterRo && !repairOrder.fromDetail;
    }

    return filterRo;
  });

  filteredRepairOrders.sort((a: IRepairOrder, b: IRepairOrder) => {
    const dropOffDateTimeA = getStringDateTime(a.dropOffDateTime);
    const dropOffDateTimeB = getStringDateTime(b.dropOffDateTime);

    switch (orderBy?.field) {
      case RepairOrderOrderByField.dropOffDateTime:
        return orderBy?.descending ? dropOffDateTimeB.diff(dropOffDateTimeA) : dropOffDateTimeA.diff(dropOffDateTimeB);
      default:
        return dropOffDateTimeB.diff(dropOffDateTimeA);
    }
  });

  return filteredRepairOrders;
};
